import { useRef, useState } from "react";
import { DropdownMenu, DropdownItem } from "./DropdownMenu";
import OpsPreview from "./OpsPreview";
import SparkleIcon from "./SparkleIcon";
import type { PreviewOp } from "@/mutations/tasks";
import { NEEDS_NETWORK, useOnline } from "@/hooks/useOnline";

export interface AiActionEntry {
  id: string;
  name: string;
  /** When true, run the preview → edit → apply flow instead of firing instantly. */
  confirm: boolean;
  /** Grok channel (present iff enabled): run instantly or via preview→apply. */
  run?: () => void | Promise<unknown>;
  /** Confirm path, step 1: run the LLM and return the proposed operations (null on error). */
  preview?: () => Promise<PreviewOp[] | null>;
  /** Confirm path, step 2: apply the operations the user kept/edited. */
  apply?: (ops: PreviewOp[]) => void | Promise<unknown>;
  /** Deliver channels (present iff enabled). */
  copy?: () => void | Promise<unknown>;
  web?: () => void | Promise<unknown>;
}

const ICONS = {
  grok: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z",
  web: "M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25",
  copy: "M9 12h3.75M9 15h3.75M15.75 18.75H18a2.25 2.25 0 002.25-2.25V6.1c0-1.14-.85-2.1-1.98-2.19M8.25 8.25H4.88c-.62 0-1.13.5-1.13 1.13v11.25c0 .62.5 1.12 1.13 1.12h9.75c.62 0 1.12-.5 1.12-1.12V9.38c0-.63-.5-1.13-1.12-1.13H8.25z",
} as const;

function ChannelButton({ label, path, onClick, disabled, hint = label }: {
  label: string; path: string; onClick: () => void; disabled: boolean; hint?: string;
}) {
  return (
    <button type="button" role="menuitem" aria-label={label} title={hint} disabled={disabled} onClick={onClick}
      className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
      </svg>
    </button>
  );
}

interface Props {
  entries: AiActionEntry[];
  busy?: boolean;
  align?: "left" | "right";
  side?: "top" | "bottom";
  ariaLabel?: string;
  buttonClassName?: string;
  iconClassName?: string;
  spinnerClassName?: string;
  testId?: string;
}

/**
 * The single entry point for every AI action in a given context: one ✨ icon that
 * ALWAYS opens a dropdown of the available actions (never one-taps, so the user picks
 * deliberately). An action flagged `confirm` runs the preview → edit → apply flow
 * (see OpsPreview); otherwise it fires instantly.
 */
export default function AiActionMenu({
  entries,
  busy = false,
  align = "left",
  side = "bottom",
  ariaLabel = "AI-действия",
  buttonClassName = "text-gray-400 hover:text-blue-500 dark:text-gray-500 cursor-pointer disabled:cursor-default disabled:opacity-60",
  iconClassName = "w-3.5 h-3.5",
  spinnerClassName = "block w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin",
  testId,
}: Props) {
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [preview, setPreview] = useState<{ entry: AiActionEntry; ops: PreviewOp[] } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const online = useOnline();

  if (entries.length === 0) return null;
  const worksOffline = entries.some((entry) => entry.copy);

  async function choose(entry: AiActionEntry) {
    setOpen(false);
    if (entry.confirm && entry.preview && entry.apply) {
      setWorking(true);
      const ops = await entry.preview();
      setWorking(false);
      // Empty/null → the preview mutation already surfaced a toast (error or "nothing found").
      if (ops && ops.length > 0) setPreview({ entry, ops });
    } else {
      setWorking(true);
      try {
        await entry.run?.();
      } finally {
        setWorking(false);
      }
    }
  }

  // Deliver channels (copy/web) are instant, local actions. Close the menu on click —
  // consistent with every other menu action; feedback comes from the DetailsPanel toast.
  // setOpen(false) is a state update that doesn't preempt fn()'s synchronous window.open /
  // clipboard / share call, so the user-gesture requirement is preserved.
  async function runChannel(fn?: () => void | Promise<unknown>) {
    if (!fn) return;
    setOpen(false);
    await fn();
  }

  async function applyPreview(edited: PreviewOp[]) {
    const p = preview;
    if (!p) return;
    setWorking(true);
    try {
      await p.entry.apply?.(edited);
    } finally {
      setWorking(false);
      setPreview(null);
    }
  }

  return (
    <div className="relative inline-flex flex-shrink-0" ref={anchorRef}>
      <button
        type="button"
        aria-label={ariaLabel}
        title={online || worksOffline ? ariaLabel : NEEDS_NETWORK}
        disabled={busy || working || (!online && !worksOffline)}
        onClick={() => setOpen((v) => !v)}
        className={buttonClassName}
      >
        {busy || working ? (
          <span className={spinnerClassName} aria-hidden="true" />
        ) : (
          <SparkleIcon className={iconClassName} />
        )}
      </button>

      {open && (
        <DropdownMenu anchorRef={anchorRef} onClose={() => setOpen(false)} align={align} side={side} testId={testId}>
          {entries.map((entry) => {
            const hasDeliver = entry.web || entry.copy;
            // Execute-only entries (Zen, the list header menu) — the previous clickable row.
            if (!hasDeliver) {
              if (!entry.run) return null;
              return (
                <DropdownItem key={entry.id} onSelect={() => choose(entry)} disabled={!online} title={online ? undefined : NEEDS_NETWORK}>
                  {entry.name}
                </DropdownItem>
              );
            }
            // A deliver channel exists (details panel) — a row with channel icons.
            return (
              <div key={entry.id} role="none" className="flex items-center justify-between gap-3 px-2 py-1.5">
                <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{entry.name}</span>
                <span className="flex items-center gap-0.5 flex-shrink-0">
                  {entry.run && <ChannelButton label="Сделать в приложении" path={ICONS.grok} disabled={working || !online} hint={online ? undefined : NEEDS_NETWORK} onClick={() => choose(entry)} />}
                  {entry.web && <ChannelButton label="Открыть в вебе" path={ICONS.web} disabled={working || !online} hint={online ? undefined : NEEDS_NETWORK} onClick={() => runChannel(entry.web)} />}
                  {entry.copy && <ChannelButton label="Скопировать промпт" path={ICONS.copy} disabled={working} onClick={() => runChannel(entry.copy)} />}
                </span>
              </div>
            );
          })}
        </DropdownMenu>
      )}

      {preview && (
        <OpsPreview
          title={preview.entry.name}
          operations={preview.ops}
          busy={working}
          onApply={applyPreview}
          onCancel={() => setPreview(null)}
        />
      )}
    </div>
  );
}
